import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MeasurementComparisonResponse } from '@services/api-client/responses';
import { SavedFilterResponse } from '@services/api-client/responses';

type ScorecardMetricDef = {
    key: keyof MeasurementComparisonResponse;
    labelKey: string;
};

const GAUGE_ARC_LENGTH = 131.95; // π * 42 for a semicircle of radius 42

@Component({
    selector: 'app-scorecard-item',
    templateUrl: './scorecard-item.component.html',
    styleUrls: ['./scorecard-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScorecardItemComponent {
    @Input() public availableFilters: SavedFilterResponse[] = [];
    @Input() public filterId: string | null = null;
    @Input() public data: MeasurementComparisonResponse | null = null;
    @Input() public baseData: MeasurementComparisonResponse | null = null;
    @Input() public isLoading = false;
    @Input() public externalLabel: string | null = null;
    @Input() public accentColor: string | null = null;
    @Input() public hideFilterSelect = false;
    @Output() public readonly filterSelected = new EventEmitter<string | null>();

    public readonly metrics: ScorecardMetricDef[] = [
        {
            key: 'monitorUntilAcceptableRecovery',
            labelKey: 'FEATURES.SCORECARD.METRICS.MONITOR_UNTIL_ACCEPTABLE_RECOVERY'
        },
        { key: 'goodSignalStrength', labelKey: 'FEATURES.SCORECARD.METRICS.SIGNAL_INTEGRITY' },
        { key: 'startPreParalytic', labelKey: 'FEATURES.SCORECARD.METRICS.START_PRE_PARALYTIC' }
    ];

    public readonly gaugeArcLength = GAUGE_ARC_LENGTH;

    public get gaugeDashOffset(): number {
        if (!this.data) return this.gaugeArcLength;
        const tQi = this.data.tQi ?? 0;
        return this.gaugeArcLength * (1 - tQi / 100);
    }

    public get gaugeColorClass(): string {
        if (!this.data) return '';
        const v = this.data.tQi ?? 0;
        if (v >= 90) return 'gauge-excellent';
        if (v >= 75) return 'gauge-good';
        if (v >= 50) return 'gauge-moderate';
        if (v >= 25) return 'gauge-poor';
        return 'gauge-critical';
    }

    public get tqiDelta(): number {
        if (!this.data || !this.baseData) return 0;
        return this.data.tQi - this.baseData.tQi;
    }

    public getMetricValue(metric: ScorecardMetricDef): number {
        return this.data ? (this.data[metric.key] ?? 0) : 0;
    }

    public getDelta(metric: ScorecardMetricDef): number {
        if (!this.data || !this.baseData) return 0;
        return this.data[metric.key] - this.baseData[metric.key];
    }

    public formatDelta(delta: number): string {
        if (delta > 0) return `+${delta.toFixed(1)}`;
        return delta.toFixed(1);
    }

    public onFilterChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        this.filterSelected.emit(select.value || null);
    }

    public trackById(_index: number, filter: SavedFilterResponse): string {
        return filter.id;
    }
}
